import React, { useState, useEffect } from 'react';
import { formatET } from '@/utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  Layers,
  Video,
  Building2,
  DollarSign,
  Clock,
  X,
  GripVertical,
} from 'lucide-react';
import { adminAPI, industriesAPI } from '@/services/api';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/common/SearchableSelect';

export default function AdminSeriesPage() {
  const [activeTab, setActiveTab] = useState('webinar');
  const [webinarSeries, setWebinarSeries] = useState([]);
  const [seminarSeries, setSeminarSeries] = useState([]);
  const [availableWebinars, setAvailableWebinars] = useState([]);
  const [availableSeminars, setAvailableSeminars] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    short_description: '',
    thumbnail_url: '',
    overview: '',
    why_attend: '',
    who_should_attend: '',
    item_ids: [],
    series_price: 0,
    discount_percentage: 0,
    industry_id: '',
    is_featured: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [webSeriesRes, semSeriesRes, webinarsRes, seminarsRes, industriesRes] = await Promise.all([
        adminAPI.getWebinarSeries(),
        adminAPI.getSeminarSeries(),
        adminAPI.getAvailableWebinars(),
        adminAPI.getAvailableSeminars(),
        industriesAPI.getAll(),
      ]);
      setWebinarSeries(webSeriesRes.data || []);
      setSeminarSeries(semSeriesRes.data || []);
      setAvailableWebinars(webinarsRes.data || []);
      setAvailableSeminars(seminarsRes.data || []);
      setIndustries(industriesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load series data');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').trim();
  };

  const handleOpenDialog = (series = null, type = activeTab) => {
    if (series) {
      setEditingSeries({ ...series, type });
      setFormData({
        title: series.title || '',
        slug: series.slug || '',
        description: series.description || '',
        short_description: series.short_description || '',
        thumbnail_url: series.thumbnail_url || '',
        overview: series.overview || '',
        why_attend: series.why_attend || '',
        who_should_attend: series.who_should_attend || '',
        item_ids: type === 'webinar' ? (series.webinar_ids || []) : (series.seminar_ids || []),
        series_price: series.series_price || 0,
        discount_percentage: series.discount_percentage || 0,
        industry_id: series.industry_id || '',
        is_featured: series.is_featured || false,
      });
    } else {
      setEditingSeries(null);
      setFormData({
        title: '',
        slug: '',
        description: '',
        short_description: '',
        thumbnail_url: '',
        overview: '',
        why_attend: '',
        who_should_attend: '',
        item_ids: [],
        series_price: 0,
        discount_percentage: 0,
        industry_id: '',
        is_featured: false,
      });
    }
    setShowDialog(true);
  };

  const handleSaveSeries = async () => {
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setSaving(true);
      const type = editingSeries?.type || activeTab;
      
      const data = {
        title: formData.title,
        slug: formData.slug || generateSlug(formData.title),
        description: formData.description,
        short_description: formData.short_description,
        thumbnail_url: formData.thumbnail_url,
        overview: formData.overview,
        why_attend: formData.why_attend,
        who_should_attend: formData.who_should_attend,
        series_price: parseFloat(formData.series_price),
        discount_percentage: parseFloat(formData.discount_percentage),
        industry_id: formData.industry_id || null,
      };

      if (type === 'webinar') {
        data.webinar_ids = formData.item_ids;
        if (editingSeries) {
          await adminAPI.updateWebinarSeries(editingSeries.id, data);
          toast.success('Webinar series updated successfully');
        } else {
          const response = await adminAPI.createWebinarSeries(data);
          toast.success(`Webinar series created! Code: ${response.data.series_code}`);
        }
      } else {
        data.seminar_ids = formData.item_ids;
        if (editingSeries) {
          await adminAPI.updateSeminarSeries(editingSeries.id, data);
          toast.success('Seminar series updated successfully');
        } else {
          const response = await adminAPI.createSeminarSeries(data);
          toast.success(`Seminar series created! Code: ${response.data.series_code}`);
        }
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save series');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeries = async (series, type) => {
    if (!window.confirm(`Are you sure you want to delete "${series.title}"?`)) {
      return;
    }
    try {
      if (type === 'webinar') {
        await adminAPI.deleteWebinarSeries(series.id);
      } else {
        await adminAPI.deleteSeminarSeries(series.id);
      }
      toast.success('Series deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete series');
    }
  };

  const toggleItemSelection = (itemId) => {
    if (formData.item_ids.includes(itemId)) {
      setFormData({
        ...formData,
        item_ids: formData.item_ids.filter(id => id !== itemId)
      });
    } else {
      setFormData({
        ...formData,
        item_ids: [...formData.item_ids, itemId]
      });
    }
  };

  const getAvailableItems = () => {
    const type = editingSeries?.type || activeTab;
    return type === 'webinar' ? availableWebinars : availableSeminars;
  };

  const renderSeriesTable = (seriesList, type) => {
    const filteredSeries = seriesList.filter(s => 
      s.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.series_code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filteredSeries.length === 0) {
      return (
        <div className="text-center py-12">
          <Layers className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-slate-500">No {type} series found</p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Title</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Revenue</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredSeries.map((series) => (
            <TableRow key={series.id} className="hover:bg-slate-50/50">
              <TableCell>
                <div className="flex items-center gap-3">
                  {series.thumbnail_url ? (
                    <img 
                      src={series.thumbnail_url} 
                      alt="" 
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-navy-700">{series.title}</p>
                    <p className="text-sm text-slate-500">{series.series_code}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge className="bg-navy-100 text-navy-700">
                  {type === 'webinar' ? series.webinar_count : series.seminar_count} items
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{series.series_price}</span>
                </div>
              </TableCell>
              <TableCell>
                {series.discount_percentage > 0 ? (
                  <Badge className="bg-green-100 text-green-700">
                    {series.discount_percentage}% off
                  </Badge>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </TableCell>
              <TableCell>
                <span className="font-medium">${series.total_revenue || 0}</span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {series.is_featured && (
                    <Star className="w-4 h-4 fill-gold-500 text-gold-500" />
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleOpenDialog(series, type)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDeleteSeries(series, type)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-series">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Series Management</h2>
          <p className="text-slate-500">Bundle webinars and seminars into series</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-navy-600 hover:bg-navy-700 rounded-xl"
          data-testid="add-series-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Series
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 rounded-xl">
        <CardContent className="p-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by title or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-lg"
            />
          </div>
        </CardContent>
      </Card>

      {/* Series Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-72">
          <TabsTrigger value="webinar" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Webinar Series
          </TabsTrigger>
          <TabsTrigger value="seminar" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Seminar Series
          </TabsTrigger>
        </TabsList>

        <TabsContent value="webinar" className="mt-4">
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
                </div>
              ) : (
                renderSeriesTable(webinarSeries, 'webinar')
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="seminar" className="mt-4">
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
                </div>
              ) : (
                renderSeriesTable(seminarSeries, 'seminar')
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingSeries ? 'Edit Series' : `Create New ${activeTab === 'webinar' ? 'Webinar' : 'Seminar'} Series`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter series title"
                />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="auto-generated-from-title"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Series description"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Overview</Label>
              <Textarea
                value={formData.overview}
                onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
                placeholder="Detailed overview of the series"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Why Attend</Label>
                <Textarea
                  value={formData.why_attend}
                  onChange={(e) => setFormData({ ...formData, why_attend: e.target.value })}
                  placeholder="Key reasons to attend"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Who Should Attend</Label>
                <Textarea
                  value={formData.who_should_attend}
                  onChange={(e) => setFormData({ ...formData, who_should_attend: e.target.value })}
                  placeholder="Target audience"
                  rows={3}
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Series Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.series_price}
                  onChange={(e) => setFormData({ ...formData, series_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Industry</Label>
                <SearchableSelect
                  options={industries.map((industry) => ({ value: industry.id, label: industry.name }))}
                  value={formData.industry_id}
                  onValueChange={(value) => setFormData({ ...formData, industry_id: value })}
                  placeholder="Select industry"
                  searchPlaceholder="Search industries..."
                  dataTestId="industry-select"
                  preserveOrder={true}
                />
              </div>
            </div>

            {/* Item Selection */}
            <div className="space-y-3">
              <Label>
                Select {(editingSeries?.type || activeTab) === 'webinar' ? 'Webinars' : 'Seminars'} to Include
              </Label>
              <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                {getAvailableItems().length === 0 ? (
                  <p className="text-slate-500 text-center py-4">
                    No available items. Create some {(editingSeries?.type || activeTab) === 'webinar' ? 'webinars' : 'seminars'} first.
                  </p>
                ) : (
                  getAvailableItems().map((item) => (
                    <div 
                      key={item.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.item_ids.includes(item.id) 
                          ? 'border-navy-500 bg-navy-50' 
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => toggleItemSelection(item.id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.item_ids.includes(item.id)}
                        onChange={() => {}}
                        className="rounded border-slate-300"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-navy-700">{item.title}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          {item.scheduled_date && (
                            <span>{formatET(item.scheduled_date, 'MMM dd, yyyy')}</span>
                          )}
                          <span>${item.price}</span>
                          {item.cpe_credits > 0 && (
                            <Badge className="bg-gold-100 text-gold-700 text-xs">
                              {item.cpe_credits} CPE
                            </Badge>
                          )}
                        </div>
                      </div>
                      {formData.item_ids.includes(item.id) && (
                        <Badge className="bg-navy-500 text-white">
                          #{formData.item_ids.indexOf(item.id) + 1}
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
              {formData.item_ids.length > 0 && (
                <p className="text-sm text-slate-500">
                  {formData.item_ids.length} item(s) selected
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is_featured">Featured Series</Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSeries}
              disabled={saving}
              className="bg-navy-600 hover:bg-navy-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingSeries ? 'Update Series' : 'Create Series'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
